import { useState } from 'react';
import { toast } from 'sonner';
import { Send, Mail, Phone, MapPin, Clock, Facebook, Instagram, Linkedin } from 'lucide-react';
import axios from 'axios';
import NewsletterForm from '@/components/NewsletterForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const socialLinks = [
  { name: 'Facebook', icon: Facebook, url: 'https://facebook.com/NextGenTechPath' },
  { name: 'Instagram', icon: Instagram, url: 'https://instagram.com/NextGenTechPath' },
  { name: 'LinkedIn', icon: Linkedin, url: 'https://linkedin.com/company/nextgentechpath' },
];

const Contact = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "bg-[#060F22] border border-[#00AEEF]/30 text-white placeholder-[#94A3B8]/50 focus:border-[#FF6A00] focus:ring-1 focus:ring-[#FF6A00] rounded-xl px-4 py-3 w-full transition-all";
  const labelClass = "block text-sm font-medium text-[#94A3B8] mb-2";

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email Us',
      lines: ['info@nextgentechpath.org', 'partnerships@nextgentechpath.org']
    },
    {
      icon: Phone,
      title: 'Call Us',
      lines: ['(248) 956-6986', 'Mon-Fri, 9am-5pm EST']
    },
    {
      icon: MapPin,
      title: 'Visit Us',
      lines: ['Innovation Hub', 'Tech District, City']
    },
    {
      icon: Clock,
      title: 'Office Hours',
      lines: ['Monday - Friday', '9:00 AM - 5:00 PM EST']
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-24 sm:py-32 bg-[#060F22] relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[#0A1F44] via-[#060F22] to-[#060F22]"></div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#00FFE0] mb-6 block">
              Get in Touch
            </span>
            <h1 className="text-5xl sm:text-6xl font-black tracking-tighter text-white leading-tight mb-6">
              Let's Start a{' '}
              <span className="gradient-text">Conversation</span>
            </h1>
            <p className="text-lg sm:text-xl text-[#94A3B8] leading-relaxed">
              Have questions about our programs, partnerships, or want to learn more? We'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-24 sm:py-32 bg-[#0A1F44]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-[#0A1F44]/50 backdrop-blur-md border border-[#00AEEF]/20 rounded-2xl p-8 sm:p-10">
                <h2 className="text-2xl font-bold text-white mb-2">Send Us a Message</h2>
                <p className="text-[#94A3B8] mb-8">Fill out the form below and we'll get back to you within 1-2 business days.</p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className={labelClass}>Your Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        data-testid="contact-name-input"
                        className={inputClass}
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        data-testid="contact-email-input"
                        className={inputClass}
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>Subject *</label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      data-testid="contact-subject-input"
                      className={inputClass}
                      placeholder="How can we help?"
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Message *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      data-testid="contact-message-input"
                      className={`${inputClass} min-h-[160px] resize-none`}
                      placeholder="Tell us more about your inquiry..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-btn"
                    className="inline-flex items-center justify-center w-full bg-[#FF6A00] text-white font-bold rounded-xl px-8 py-4 shadow-[0_0_20px_rgba(255,106,0,0.3)] hover:shadow-[0_0_30px_rgba(255,106,0,0.5)] hover:-translate-y-1 transition-all duration-300 gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></span>
                    ) : (
                      <>
                        Send Message
                        <Send className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-6">
              {contactInfo.map((info, index) => (
                <div
                  key={index}
                  className="bg-[#0A1F44]/50 backdrop-blur-md border border-[#00AEEF]/20 rounded-2xl p-6 hover:border-[#00FFE0]/50 transition-all duration-500"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#00AEEF]/20 flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-6 h-6 text-[#00AEEF]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white mb-1">{info.title}</h3>
                      {info.lines.map((line, i) => (
                        <p key={i} className="text-[#94A3B8] text-sm">{line}</p>
                      ))}
                    </div>
                  </div>
                </div>
              ))}

              {/* Social Links */}
              <div className="bg-[#0A1F44]/50 backdrop-blur-md border border-[#00AEEF]/20 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">Follow Us</h3>
                <p className="text-[#94A3B8] text-sm mb-4">Stay connected for updates, success stories, and more.</p>
                <div className="flex gap-3">
                  {socialLinks.map((social) => (
                    <a
                      key={social.name}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`social-${social.name.toLowerCase()}`}
                      className="w-10 h-10 rounded-lg bg-[#060F22] border border-[#00AEEF]/30 flex items-center justify-center text-[#94A3B8] hover:text-[#00FFE0] hover:border-[#00FFE0] transition-all"
                    >
                      <social.icon className="w-5 h-5" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 sm:py-32 bg-[#060F22]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#00FFE0] mb-4 block">
              FAQs
            </span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-6">
              Common Questions
            </h2>
          </div>

          <div className="space-y-6">
            {[
              {
                q: 'Who can enroll in your programs?',
                a: 'Our programs are designed for youth ages 10-25, regardless of prior coding experience. We welcome complete beginners!'
              },
              {
                q: 'How much do programs cost?',
                a: 'Thanks to our partners and donors, we offer programs at no cost or significantly reduced tuition for eligible students. Contact us for details.'
              },
              {
                q: 'What equipment do students need?',
                a: 'Students need access to a computer with internet. We can provide equipment for students who need it—just let us know when enrolling.'
              },
              {
                q: 'How can my company become a partner?',
                a: 'We\'d love to discuss partnership opportunities! Reach out via the contact form or email partnerships@nextgentechpath.org.'
              }
            ].map((faq, index) => (
              <div
                key={index}
                className="bg-[#0A1F44]/50 backdrop-blur-md border border-[#00AEEF]/20 rounded-2xl p-6"
              >
                <h3 className="text-lg font-bold text-white mb-2">{faq.q}</h3>
                <p className="text-[#94A3B8]">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-24 sm:py-32 bg-[#0A1F44]/30">
        <div className="max-w-2xl mx-auto px-6 lg:px-12 text-center">
          <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#00FFE0] mb-4 block">
            Stay Connected
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white mb-4">
            Subscribe to Our Newsletter
          </h2>
          <p className="text-[#94A3B8] mb-8">
            Get updates on enrollment dates, events, success stories, and more.
          </p>
          <NewsletterForm />
        </div>
      </section>
    </div>
  );
};

export default Contact;
